"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TagsFieldLogic = void 0;
const types_1 = require("@smartsuite/types");
const difference_1 = __importDefault(require("lodash-es/difference"));
const isArray_1 = __importDefault(require("lodash-es/isArray"));
const xor_1 = __importDefault(require("lodash-es/xor"));
const clipboard_1 = require("../../clipboard/clipboard");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const tags_fieldtype_1 = require("../../safe-field-value/list-fields/tags-field/tags.fieldtype");
const filter_1 = require("../../utils/filter");
const intl_1 = require("../../locale/intl");
const texts_1 = require("../../texts");
const validation_1 = require("../../validation/validation");
const migrations_1 = require("../../migrations/migrations");
class TagsFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "tagsfield";
        this.label = (0, intl_1.$t)(texts_1.tagsFieldText.tag);
        this.icon = "tag";
        this.iconColor = "#4ECCFD";
        this.placeholder = (0, intl_1.$t)(texts_1.tagsFieldText.specify_tags);
        this.searchSynonyms = [(0, intl_1.$t)(texts_1.tagsFieldText.hashtag), "#"];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            has_all_of: comparisons_1.hasAllOf,
            has_any_of: comparisons_1.hasAnyOf,
            is_exactly: comparisons_1.isExactly,
            has_none_of: comparisons_1.hasNoneOf,
        };
        this.fieldValueGetter = new tags_fieldtype_1.TagsFieldValueGetter();
        this.clipboardPasteRules = {
            tagsfield: clipboard_1.clipboardTagToSelf,
            // List fields
            multipleselectfield: clipboard_1.clipboardListFieldsToTag,
            singleselectfield: clipboard_1.clipboardListFieldsToTag,
            // Reference fields
            userfield: clipboard_1.clipboardMembersToTag,
            linkedrecordfield: clipboard_1.clipboardReferenceToTag,
            // Other fields
            phonefield: clipboard_1.clipboardPhoneToTag,
            ipaddressfield: clipboard_1.clipboardIPToTag,
            recordidfield: clipboard_1.clipboardRecordIdToTag,
            colorpickerfield: clipboard_1.clipboardColorPickerToTag,
            // Any to tag block
            yesnofield: clipboard_1.clipboardAnyToTag,
            statusfield: clipboard_1.clipboardAnyToTag,
            fullnamefield: clipboard_1.clipboardAnyToTag,
            emailfield: clipboard_1.clipboardAnyToTag,
            linkfield: clipboard_1.clipboardAnyToTag,
            firstcreatedfield: clipboard_1.clipboardAnyToTag,
            lastupdatedfield: clipboard_1.clipboardAnyToTag,
            textareafield: clipboard_1.clipboardAnyToTag,
            textfield: clipboard_1.clipboardTextToTag,
            recordtitlefield: clipboard_1.clipboardTextToTag,
            richtextareafield: clipboard_1.clipboardAnyToTag,
            unknownfield: clipboard_1.clipboardAnyToTag,
        };
    }
    get validators() {
        const uniqueValidator = (0, validation_1.uniqueRecordFieldValueWithContext)({
            checkUniqueness: this.appRecordsService.checkUniqueness.bind(this.appRecordsService),
        });
        return { required: validation_1.required, unique: uniqueValidator };
    }
    getCssClass(field) {
        if (field.params.control_type === "radiobutton") {
            return "record-group--poll";
        }
        return "";
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            switchToInputControl: "OnCellSelect",
            saveKeys: types_1.navigationKeys,
            editOnStartTyping: false,
            editorWidth: "100%",
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnModalOpen",
        };
    }
    getEmptyValue() {
        return [];
    }
    sortKey(fieldValue, field) {
        const valuesOrder = (fieldValue || []).map((value) => {
            var _a;
            const choice = (0, fields_1.getFieldChoiceByValue)(field, value);
            if (choice) {
                return (_a = choice.value_order) !== null && _a !== void 0 ? _a : choice === null || choice === void 0 ? void 0 : choice.label;
            }
            const tag = this.appRecordsService.getTagById(value);
            return tag === null || tag === void 0 ? void 0 : tag.label;
        });
        return valuesOrder.join(" ");
    }
    valueToString(fieldValue, field) {
        var _a;
        const choices = (_a = field.params.choices) !== null && _a !== void 0 ? _a : [];
        const values = (fieldValue || []).map((tagId) => {
            var _a, _b;
            const choice = choices.find(({ value }) => tagId === value);
            return (_a = choice === null || choice === void 0 ? void 0 : choice.label) !== null && _a !== void 0 ? _a : (_b = this.appRecordsService.getTagById(tagId)) === null || _b === void 0 ? void 0 : _b.label;
        });
        return values
            .filter((value) => !!value)
            .sort()
            .join(", ");
    }
    getGroupValues(fieldValue) {
        if (!(0, isArray_1.default)(fieldValue) || !fieldValue.length) {
            return [[]];
        }
        return fieldValue
            .filter(filter_1.filterUniqueValues)
            .map((valueItem) => [valueItem]);
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            placeholder: (0, intl_1.$t)(texts_1.tagsFieldText.select_from_list),
            choices: field.params.choices,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = (0, migrations_1.migrateBaseField)(fieldTo, fieldFrom);
        const tagChoices = ((_a = fieldFrom.params.choices) !== null && _a !== void 0 ? _a : []).map((choice, index) => ({
            value: choice.value,
            label: choice.label,
            value_order: index,
            is_default: true,
        }));
        return {
            ...migratedField,
            params: { ...migratedField.params, choices: tagChoices },
        };
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        const removedValues = (0, difference_1.default)(previousValue, currentValue);
        const addedValues = (0, difference_1.default)(currentValue, previousValue);
        return { previousValue: removedValues, currentValue: addedValues };
    }
    isValueEqual(fieldValue1, fieldValue2, field) {
        const value1 = fieldValue1 !== null && fieldValue1 !== void 0 ? fieldValue1 : [];
        const value2 = fieldValue2 !== null && fieldValue2 !== void 0 ? fieldValue2 : [];
        if (value1.length !== value2.length) {
            return false;
        }
        const tags1 = value1.map((tag) => { var _a; return (_a = this.getTagIdByValueOrLabel(tag, field)) !== null && _a !== void 0 ? _a : tag; });
        const tags2 = value2.map((tag) => { var _a; return (_a = this.getTagIdByValueOrLabel(tag, field)) !== null && _a !== void 0 ? _a : tag; });
        const uniqValues = (0, xor_1.default)(tags1, tags2);
        return uniqValues.length === 0;
    }
    /**
     * Adds missing tags to field choices before putting value to clipboard.
     * */
    putValueToClipboard(clipboard, fieldValue, field, userPreferences, record) {
        var _a;
        const extraChoices = fieldValue
            .map((tagId) => {
            var _a, _b;
            if ((_a = field.params.choices) === null || _a === void 0 ? void 0 : _a.some(({ value }) => value === tagId)) {
                return null;
            }
            const tag = this.appRecordsService.getTagById(tagId);
            return {
                value: tagId,
                label: (_b = tag === null || tag === void 0 ? void 0 : tag.label) !== null && _b !== void 0 ? _b : "",
            };
        })
            .filter((choice) => !!choice);
        const updatedField = {
            ...field,
            params: {
                ...field.params,
                choices: (_a = field.params.choices) === null || _a === void 0 ? void 0 : _a.concat(extraChoices),
            },
        };
        super.putValueToClipboard(clipboard, fieldValue, updatedField, userPreferences, record);
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    filterToRecordValue(filterValue, comparison, field) {
        const validComparison = ["is_exactly", "has_all_of"];
        if (Array.isArray(filterValue) && validComparison.includes(comparison)) {
            return filterValue;
        }
        throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
    }
    getTagIdByValueOrLabel(valueOrLabel, field) {
        const choiceByValue = (0, fields_1.getFieldChoiceByValue)(field, valueOrLabel);
        if (choiceByValue !== null) {
            return String(choiceByValue.value);
        }
        const choiceByLabel = (0, fields_1.getFieldChoiceByLabel)(field, valueOrLabel);
        if (choiceByLabel !== null) {
            return String(choiceByLabel.value);
        }
        const tagById = this.appRecordsService.getTagById(valueOrLabel);
        if (tagById !== undefined) {
            return tagById.id;
        }
        const tagByLabel = this.appRecordsService.getTagByLabel(valueOrLabel);
        if (tagByLabel !== undefined) {
            return tagByLabel.id;
        }
        return null;
    }
}
exports.TagsFieldLogic = TagsFieldLogic;
